import { useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  ColorType,
  CrosshairMode,
  LineStyle,
  type IChartApi,
  type ISeriesApi,
  type Time,
  type UTCTimestamp,
  createChart,
} from "lightweight-charts";
import type { Bars } from "../api";

export type ChartOverlay = {
  /** Inclusive index range (into `bars.t`) for the spike band. */
  spike?: [number, number];
  /** Inclusive index range for the range/consolidation band. */
  range?: [number, number];
  /** Horizontal price lines: TR high/low and measured-move target. */
  tr_high?: number;
  tr_low?: number;
  target?: number;
  /** Index into `bars.t` where breakout occurred, plus its direction. */
  breakout?: { idx: number; with_trend: boolean };
};

const INTRADAY_RE = /min|hour/i;

// Target pixels per bar. Window count is derived from the chart's actual
// width so each bar renders at the same width regardless of orientation —
// landscape just shows more bars, not wider ones.
const TARGET_BAR_PX = 5;
const MIN_WINDOW_BARS = 40;

function useTheme(): "dark" | "light" {
  const [theme, setTheme] = useState<"dark" | "light">(() => {
    if (typeof document === "undefined") return "dark";
    return (document.documentElement.dataset.theme as "dark" | "light") || "dark";
  });
  useEffect(() => {
    const obs = new MutationObserver(() => {
      const t = document.documentElement.dataset.theme;
      setTheme(t === "light" ? "light" : "dark");
    });
    obs.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
    return () => obs.disconnect();
  }, []);
  return theme;
}

function useContainerWidth(ref: React.RefObject<HTMLElement>): number {
  const [w, setW] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    setW(el.clientWidth);
    const ro = new ResizeObserver((entries) => {
      const cw = entries[0]?.contentRect.width ?? el.clientWidth;
      setW(cw);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return w;
}

function toLwcTime(iso: string, tf: string): Time {
  if (INTRADAY_RE.test(tf)) {
    return Math.floor(new Date(iso).getTime() / 1000) as UTCTimestamp;
  }
  // Daily / weekly / monthly: lightweight-charts expects 'YYYY-MM-DD'.
  return iso.slice(0, 10) as unknown as Time;
}

function isComplete(o: number | null, h: number | null, l: number | null, c: number | null) {
  return o !== null && h !== null && l !== null && c !== null;
}

export default function Chart({
  bars,
  overlay,
  height = 380,
  reserve = 160,
  onBarTap,
  onCrosshairMove,
}: {
  bars: Bars;
  overlay?: ChartOverlay;
  height?: number;
  /**
   * Pixels to reserve below the viewport for surrounding UI (other content
   * above + sticky footers). Used to compute the chart's max height so the
   * time axis doesn't slide under footer buttons.
   */
  reserve?: number;
  /** Called with the bar index (in `bars.t`) when the user taps a candle. */
  onBarTap?: (idx: number) => void;
  /** Called as the crosshair moves; null when it leaves the chart. */
  onCrosshairMove?: (idx: number | null) => void;
}) {
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const seriesRef = useRef<ISeriesApi<"Candlestick"> | null>(null);
  const onTapRef = useRef(onBarTap);
  onTapRef.current = onBarTap;
  const onHoverRef = useRef(onCrosshairMove);
  onHoverRef.current = onCrosshairMove;
  const containerW = useContainerWidth(wrapRef);
  const windowBars = Math.max(
    MIN_WINDOW_BARS,
    Math.floor(containerW / TARGET_BAR_PX) || MIN_WINDOW_BARS,
  );
  const theme = useTheme();

  // Mounted-once chart (rebuilds on theme switch)
  useLayoutEffect(() => {
    if (!wrapRef.current) return;
    const cs = getComputedStyle(document.documentElement);
    const v = (name: string, fallback: string) =>
      cs.getPropertyValue(name).trim() || fallback;
    const bg = v("--chart-bg", "#0b0d10");
    const text = v("--chart-text", "#c8ccd1");
    const gridSoft = v("--chart-grid-soft", "rgba(255,255,255,0.04)");
    const grid = v("--chart-grid", "rgba(255,255,255,0.06)");
    const axis = v("--chart-axis", "#1f2329");
    const chart = createChart(wrapRef.current, {
      height,
      autoSize: true,
      layout: {
        background: { type: ColorType.Solid, color: bg },
        textColor: text,
        fontSize: 11,
      },
      grid: {
        vertLines: { color: gridSoft },
        // Lightweight-charts pins horz grids 1:1 to price-axis ticks.
        horzLines: { color: grid },
      },
      crosshair: { mode: CrosshairMode.Normal },
      // Disable vertical-axis drag-to-scale; we autoscale per visible window.
      handleScale: {
        axisPressedMouseMove: { price: false, time: true },
        axisDoubleClickReset: { price: false, time: true },
      },
      // Let vertical touch drags fall through to the browser so the page
      // can scroll when the user swipes up/down inside the chart area.
      // Horizontal drags still pan the time axis.
      handleScroll: {
        vertTouchDrag: false,
        horzTouchDrag: true,
        mouseWheel: true,
        pressedMouseMove: true,
      },
      rightPriceScale: { visible: false },
      leftPriceScale: {
        visible: true,
        borderVisible: false,
        borderColor: axis,
        entireTextOnly: true,
        minimumWidth: 44,
        scaleMargins: { top: 0.04, bottom: 0.04 },
      },
      timeScale: {
        borderColor: axis,
        rightOffset: 4,
        barSpacing: TARGET_BAR_PX,
        // Don't let the user pan into the empty space past the first/last bar.
        fixLeftEdge: true,
        fixRightEdge: true,
        lockVisibleTimeRangeOnResize: true,
      },
    });
    const series = chart.addCandlestickSeries({
      priceScaleId: "left",
      upColor: "#2ecc71",
      downColor: "#e74c3c",
      borderUpColor: "#2ecc71",
      borderDownColor: "#e74c3c",
      wickUpColor: "#2ecc71",
      wickDownColor: "#e74c3c",
      priceLineVisible: false,
      lastValueVisible: false,
      // Compute y-range from visible candles only, ignoring price lines.
      // Without this, scrolling far from TR high/low/target squashes bars.
      autoscaleInfoProvider: () => {
        const data = barsRef.current;
        if (!data) return null;
        const r = chart.timeScale().getVisibleLogicalRange();
        if (!r) return null;
        const lo = Math.max(0, Math.floor(r.from));
        const hi = Math.min(data.t.length - 1, Math.ceil(r.to));
        let min = Infinity;
        let max = -Infinity;
        for (let i = lo; i <= hi; i++) {
          const l = data.l[i];
          const h = data.h[i];
          if (l !== null && l < min) min = l;
          if (h !== null && h > max) max = h;
        }
        if (!isFinite(min) || !isFinite(max)) return null;
        const pad = (max - min) * 0.08 || max * 0.005 || 1;
        return {
          priceRange: { minValue: min - pad, maxValue: max + pad },
        };
      },
    });
    chartRef.current = chart;
    seriesRef.current = series;

    const findIdx = (time: Time) => {
      const data = barsRef.current;
      if (!data) return -1;
      return data.t.findIndex((iso) => toLwcTime(iso, data.timeframe) === time);
    };

    chart.subscribeClick((p) => {
      if (!p.time || !onTapRef.current) return;
      const idx = findIdx(p.time);
      if (idx >= 0) onTapRef.current(idx);
    });

    chart.subscribeCrosshairMove((p) => {
      const fn = onHoverRef.current;
      if (!fn) return;
      if (!p.time) {
        fn(null);
        return;
      }
      const idx = findIdx(p.time);
      fn(idx >= 0 ? idx : null);
    });

    // Force the data-push effect to re-run and re-fit on the fresh chart.
    fitForBarsRef.current = null;

    return () => {
      chart.remove();
      chartRef.current = null;
      seriesRef.current = null;
    };
  }, [height, theme]);

  // Keep latest bars accessible from the click handler without re-binding.
  const barsRef = useRef<Bars | null>(null);
  barsRef.current = bars;
  // Tracks the bars object we last auto-fit for. We only re-fit when bars
  // changes (new symbol/tf or first load) — overlay updates (e.g. tapping
  // anchors, or Reset clearing them) must not yank the view back.
  const fitForBarsRef = useRef<Bars | null>(null);
  const lastWindowBarsRef = useRef<number>(0);

  // Push candle data + overlays whenever bars or overlay change.
  const [bandStyles, setBandStyles] = useState<{
    spike?: { left: number; width: number };
    range?: { left: number; width: number };
  }>({});

  useEffect(() => {
    const chart = chartRef.current;
    const series = seriesRef.current;
    if (!chart || !series) return;

    chart.applyOptions({
      timeScale: {
        timeVisible: INTRADAY_RE.test(bars.timeframe),
        secondsVisible: false,
      },
    });

    const data = [];
    for (let i = 0; i < bars.t.length; i++) {
      if (!isComplete(bars.o[i], bars.h[i], bars.l[i], bars.c[i])) continue;
      data.push({
        time: toLwcTime(bars.t[i], bars.timeframe),
        open: bars.o[i] as number,
        high: bars.h[i] as number,
        low: bars.l[i] as number,
        close: bars.c[i] as number,
      });
    }
    series.setData(data);

    // Clear and re-create price lines for the overlay values.
    // (lightweight-charts has no "remove all"; we recreate the series.)
    // Cheaper: track and remove explicitly.
    const lines: ReturnType<ISeriesApi<"Candlestick">["createPriceLine"]>[] = [];
    if (overlay?.tr_high !== undefined) {
      lines.push(
        series.createPriceLine({
          price: overlay.tr_high,
          color: "rgba(255,165,0,0.7)",
          lineWidth: 1,
          lineStyle: LineStyle.Dotted,
          axisLabelVisible: true,
          title: "",
        }),
      );
    }
    if (overlay?.tr_low !== undefined) {
      lines.push(
        series.createPriceLine({
          price: overlay.tr_low,
          color: "rgba(255,165,0,0.7)",
          lineWidth: 1,
          lineStyle: LineStyle.Dotted,
          axisLabelVisible: true,
          title: "",
        }),
      );
    }
    if (overlay?.target !== undefined) {
      lines.push(
        series.createPriceLine({
          price: overlay.target,
          color: "rgba(46,204,113,0.9)",
          lineWidth: 2,
          lineStyle: LineStyle.Dashed,
          axisLabelVisible: true,
          title: "",
        }),
      );
    }

    if (overlay?.breakout) {
      const t = toLwcTime(bars.t[overlay.breakout.idx], bars.timeframe);
      series.setMarkers([
        {
          time: t,
          position: overlay.breakout.with_trend ? "belowBar" : "aboveBar",
          color: overlay.breakout.with_trend ? "#2ecc71" : "#e74c3c",
          shape: overlay.breakout.with_trend ? "arrowUp" : "arrowDown",
          text: overlay.breakout.with_trend ? "BO" : "fail",
        },
      ]);
    } else {
      series.setMarkers([]);
    }

    // Auto-fit when bars change (new symbol/tf) OR when the container width
    // changes orientation/window-count enough that bars would otherwise
    // stretch. Center is preserved across orientation flips so the user's
    // focus area stays put — only the visible count adjusts.
    const last = bars.t.length - 1;
    const half = windowBars / 2;
    if (fitForBarsRef.current !== bars) {
      fitForBarsRef.current = bars;
      lastWindowBarsRef.current = windowBars;
      let center: number;
      if (overlay?.spike || overlay?.range || overlay?.breakout) {
        const lo = overlay?.spike?.[0] ?? overlay?.range?.[0] ?? 0;
        const hi =
          overlay?.breakout?.idx ?? overlay?.range?.[1] ?? overlay?.spike?.[1] ?? last;
        center = (lo + hi) / 2;
      } else {
        center = last - half * 0.2;
      }
      chart.timeScale().setVisibleLogicalRange({
        from: Math.max(0, center - half),
        to: Math.min(last, center + half),
      });
    } else if (lastWindowBarsRef.current !== windowBars) {
      const cur = chart.timeScale().getVisibleLogicalRange();
      const center = cur ? (cur.from + cur.to) / 2 : last - half * 0.2;
      lastWindowBarsRef.current = windowBars;
      chart.timeScale().setVisibleLogicalRange({
        from: Math.max(0, center - half),
        to: Math.min(last, center + half),
      });
    }

    // Position the spike/range translucent bands as DOM overlays
    // (lightweight-charts has no native vertical-region primitive).
    const recomputeBands = () => {
      const ts = chart.timeScale();
      // timeToCoordinate is relative to the candle pane, which sits to the
      // right of the left-side price scale. The bands are absolutely
      // positioned inside .chart-wrap, so we offset by the price-scale
      // width to align them with the bars.
      const xOffset = chart.priceScale("left").width();
      const next: typeof bandStyles = {};
      const project = (lo: number, hi: number) => {
        const a = ts.timeToCoordinate(toLwcTime(bars.t[lo], bars.timeframe));
        const b = ts.timeToCoordinate(toLwcTime(bars.t[hi], bars.timeframe));
        if (a === null || b === null) return null;
        return { left: Math.min(a, b) + xOffset, width: Math.abs(b - a) };
      };
      if (overlay?.spike) {
        const s = project(overlay.spike[0], overlay.spike[1]);
        if (s) next.spike = s;
      }
      if (overlay?.range) {
        const r = project(overlay.range[0], overlay.range[1]);
        if (r) next.range = r;
      }
      setBandStyles(next);
    };
    recomputeBands();
    chart.timeScale().subscribeVisibleTimeRangeChange(recomputeBands);

    return () => {
      for (const l of lines) series.removePriceLine(l);
      chart.timeScale().unsubscribeVisibleTimeRangeChange(recomputeBands);
    };
  }, [bars, overlay, windowBars, theme]);

  return (
    <div
      className="chart-wrap"
      style={{
        // Fill the viewport down to the footer/below-chart UI so the time
        // axis hugs the bottom in portrait. The `height` prop is ignored as
        // a cap — only the reserve below it matters.
        height: `calc(100svh - ${reserve}px - env(safe-area-inset-top) - env(safe-area-inset-bottom))`,
        minHeight: 220,
      }}
    >
      <div ref={wrapRef} className="chart-canvas" />
      {bandStyles.spike && (
        <div
          className="chart-band chart-band-spike"
          style={{ left: bandStyles.spike.left, width: bandStyles.spike.width }}
        />
      )}
      {bandStyles.range && (
        <div
          className="chart-band chart-band-range"
          style={{ left: bandStyles.range.left, width: bandStyles.range.width }}
        />
      )}
    </div>
  );
}
